import React from 'react';
import {
  Shield, Zap, GitBranch, Lock, Package, FileCode,
  Settings, CheckCircle2, ArrowRight, Globe,
  Award, Layers, Terminal
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/shadcn/card';
import { Button } from '../components/shadcn/button';
import { Separator } from '../components/shadcn/separator';
import { CONTROL_COUNT, COVERAGE_LABEL, DOMAINS, LEAD_DIFFERENTIATORS, WHY_NOW } from '../components/marketing/content';

const Brochure: React.FC = () => {
  return (
    <div className="brochure-root min-h-screen print:text-black">
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0.5in;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print {
            display: none !important;
          }
          .page-break {
            page-break-before: always;
          }
        }
      `}</style>

      {/* Print Button */}
      <div className="no-print fixed top-4 right-4 z-50 flex gap-2">
        <Button
          onClick={() => window.print()}
          className="bg-[#1E4D38] text-[#F5F1E6] hover:bg-[#173D2C] shadow-lg"
        >
          Print / Save as PDF
        </Button>
        <Button variant="secondary" asChild>
          <a href="/">Back to Site</a>
        </Button>
      </div>

      {/* ==================== PAGE 1: Cover ==================== */}
      <div className="min-h-screen flex flex-col" style={{ background: '#1E4D38' }}>
        {/* Header */}
        <header className="p-8">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/10 rounded-xl backdrop-blur">
              <Shield className="w-8 h-8 text-[#F5F1E6]" />
            </div>
            <span className="text-2xl font-bold text-[#F5F1E6]">Paxley</span>
          </div>
        </header>

        {/* Hero */}
        <main className="flex-1 flex items-center justify-center px-8">
          <div className="text-center max-w-4xl">
            <div className="inline-block px-4 py-2 bg-white/10 rounded-full text-[#F5F1E6]/80 text-sm font-medium mb-6 backdrop-blur">
              Code Security and Code Trust Platform
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-[#F5F1E6] mb-6 leading-tight">
              Unify Security.<br />
              <span className="text-[#F5F1E6]/80">Prove Code Trust.</span>
            </h1>
            <p className="text-xl text-[#F5F1E6]/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              One platform for code, dependencies, secrets, infrastructure, containers and SBOMs, with
              evidence you can hand to customers, regulators, boards and acquirers.
            </p>
            <div className="flex items-center justify-center gap-8 text-[#F5F1E6]/80">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#F5F1E6]">12</div>
                <div className="text-sm">Scanning Engines</div>
              </div>
              <div className="w-px h-12 bg-white/20"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#F5F1E6]">{CONTROL_COUNT}</div>
                <div className="text-sm">Code Trust Controls</div>
              </div>
              <div className="w-px h-12 bg-white/20"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#F5F1E6]">0</div>
                <div className="text-sm">Per-Seat Fees</div>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="p-8 text-center text-[#F5F1E6]/80 text-sm">
          <p>Paxley Code Security Platform &bull; Nucleus Systems &bull; paxleysoftware.com</p>
        </footer>
      </div>

      {/* ==================== PAGE 2: Problem & Solution ==================== */}
      <div className="min-h-screen p-8 md:p-16 page-break">
        <div className="max-w-5xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#17150F] mb-4">Why Now</h2>
            <p className="text-lg text-[#17150F]/70 max-w-2xl mx-auto">
              The software supply chain has become the way in, and regulators now expect evidence.
            </p>
          </div>

          {/* Sourced market facts, shared with the website */}
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {WHY_NOW.map((f) => (
              <Card key={f.figure} className="bg-[#FBF8F1] border-[#E4DFD3]">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-[#1E4D38] mb-2">{f.figure}</div>
                  <p className="font-semibold text-[#17150F]">{f.title}</p>
                  <p className="mt-2 text-sm text-[#17150F]/70">{f.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mb-16 text-xs text-[#17150F]/50">
            Sources: {WHY_NOW.map((f) => f.source.label).join('; ')}.
          </p>

          {/* Solution */}
          <Card className="bg-[#1E4D38] border-0 text-[#F5F1E6]">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-white/10 rounded-xl">
                  <Shield className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">The Paxley Solution</h3>
                  <p className="text-[#F5F1E6]/80">
                    Not another scanner: an evidence layer. Paxley unifies security, protects the
                    software supply chain and turns findings into Code Trust evidence.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#F5F1E6]/80 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Supply Chain First</div>
                    <div className="text-sm text-[#F5F1E6]/80">Code, dependencies, secrets, IaC, SBOM</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#F5F1E6]/80 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">AI-Assisted Remediation</div>
                    <div className="text-sm text-[#F5F1E6]/80">A suggested fix for every finding</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#F5F1E6]/80 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Code Trust Evidence</div>
                    <div className="text-sm text-[#F5F1E6]/80">Mapped to NS-CTAF controls</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ==================== PAGE 3: Features ==================== */}
      <div className="min-h-screen p-8 md:p-16 bg-[#FBF8F1] page-break">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#17150F] mb-4">Comprehensive Security Coverage</h2>
            <p className="text-lg text-[#17150F]/70">
              One platform, multiple security scanners, complete visibility.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* SAST */}
            <Card className="bg-white shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[#E4EFE4] rounded-lg">
                    <FileCode className="w-6 h-6 text-[#1E4D38]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#17150F]">SAST Analysis</h3>
                </div>
                <p className="text-[#17150F]/70 mb-4">
                  Deep semantic code analysis to find injection flaws, XSS,
                  authentication issues, and more.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">SQL Injection</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">XSS</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">Command Injection</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">Path Traversal</span>
                </div>
              </CardContent>
            </Card>

            {/* Dependencies */}
            <Card className="bg-white shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[#E4EFE4] rounded-lg">
                    <Package className="w-6 h-6 text-[#1E4D38]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#17150F]">Dependency Scanning</h3>
                </div>
                <p className="text-[#17150F]/70 mb-4">
                  Detect known CVEs in open source dependencies with EPSS scoring, fix recommendations,
                  and license compliance checking.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">npm</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">pip</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">Maven</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">Go</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">NuGet</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">Cargo</span>
                </div>
              </CardContent>
            </Card>

            {/* Secrets */}
            <Card className="bg-white shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[#E4DFD3] rounded-lg">
                    <Lock className="w-6 h-6 text-[#17150F]/70" />
                  </div>
                  <h3 className="text-xl font-bold text-[#17150F]">Secret Detection</h3>
                </div>
                <p className="text-[#17150F]/70 mb-4">
                  Find hardcoded API keys, passwords, and tokens with 80+ detection patterns
                  including git history scanning.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-[#FBF8F1] text-[#17150F]/70 text-xs rounded">AWS Keys</span>
                  <span className="px-2 py-1 bg-[#FBF8F1] text-[#17150F]/70 text-xs rounded">API Tokens</span>
                  <span className="px-2 py-1 bg-[#FBF8F1] text-[#17150F]/70 text-xs rounded">Private Keys</span>
                  <span className="px-2 py-1 bg-[#FBF8F1] text-[#17150F]/70 text-xs rounded">Passwords</span>
                </div>
              </CardContent>
            </Card>

            {/* IaC */}
            <Card className="bg-white shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[#E4EFE4] rounded-lg">
                    <Settings className="w-6 h-6 text-[#1E4D38]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#17150F]">Infrastructure as Code</h3>
                </div>
                <p className="text-[#17150F]/70 mb-4">
                  Scan Terraform, Kubernetes, Docker, and CloudFormation for misconfigurations
                  and compliance violations.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">Terraform</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">Kubernetes</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">Docker</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">AWS</span>
                </div>
              </CardContent>
            </Card>

            {/* Container */}
            <Card className="bg-white shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-pink-100 rounded-lg">
                    <Layers className="w-6 h-6 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold text-[#17150F]">Container Scanning</h3>
                </div>
                <p className="text-[#17150F]/70 mb-4">
                  Scan Docker images for OS and application vulnerabilities before deployment
                  to production environments.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-pink-50 text-pink-700 text-xs rounded">Docker Hub</span>
                  <span className="px-2 py-1 bg-pink-50 text-pink-700 text-xs rounded">ECR</span>
                  <span className="px-2 py-1 bg-pink-50 text-pink-700 text-xs rounded">GCR</span>
                  <span className="px-2 py-1 bg-pink-50 text-pink-700 text-xs rounded">GHCR</span>
                </div>
              </CardContent>
            </Card>

            {/* SBOM */}
            <Card className="bg-white shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-[#E4EFE4] rounded-lg">
                    <Award className="w-6 h-6 text-[#1E4D38]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#17150F]">SBOM Generation</h3>
                </div>
                <p className="text-[#17150F]/70 mb-4">
                  Generate Software Bill of Materials in CycloneDX and SPDX formats for
                  compliance and supply chain security.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">CycloneDX</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">SPDX</span>
                  <span className="px-2 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs rounded">Compliance</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* ==================== PAGE 4: Integration & Workflow ==================== */}
      <div className="min-h-screen p-8 md:p-16 page-break">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#17150F] mb-4">Seamless Integration</h2>
            <p className="text-lg text-[#17150F]/70">
              Fits naturally into your existing development workflow.
            </p>
          </div>

          {/* Workflow Steps */}
          <div className="flex flex-col md:flex-row items-start justify-between gap-6 mb-16">
            <div className="flex-1 text-center">
              <div className="w-16 h-16 bg-[#E4EFE4] rounded-full flex items-center justify-center mx-auto mb-4">
                <GitBranch className="w-8 h-8 text-[#1E4D38]" />
              </div>
              <h4 className="font-bold text-lg mb-2">1. Developer Pushes Code</h4>
              <p className="text-[#17150F]/70 text-sm">Code changes trigger automatic scanning via GitHub Action or webhook</p>
            </div>
            <ArrowRight className="w-8 h-8 text-[#F5F1E6]/70 hidden md:block mt-8" />
            <div className="flex-1 text-center">
              <div className="w-16 h-16 bg-[#E4EFE4] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#1E4D38]" />
              </div>
              <h4 className="font-bold text-lg mb-2">2. Paxley Scans</h4>
              <p className="text-[#17150F]/70 text-sm">Multiple scanners analyze code, dependencies, secrets, and infrastructure</p>
            </div>
            <ArrowRight className="w-8 h-8 text-[#F5F1E6]/70 hidden md:block mt-8" />
            <div className="flex-1 text-center">
              <div className="w-16 h-16 bg-[#E4EFE4] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-[#1E4D38]" />
              </div>
              <h4 className="font-bold text-lg mb-2">3. Results in PR</h4>
              <p className="text-[#17150F]/70 text-sm">Findings appear as PR comments with fix guidance and severity ratings</p>
            </div>
          </div>

          <Separator className="my-12 bg-[#E4DFD3]" />

          {/* Integration Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-[#FBF8F1] border-[#E4DFD3]">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#17150F] mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#1E4D38]" />
                  CI/CD Integration
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    GitHub Actions native integration
                  </li>
                  <li className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    SARIF export into any CI system or code-scanning dashboard
                  </li>
                  <li className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Git URL and archive scanning, nothing to install
                  </li>
                  <li className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    REST API for custom integrations
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-[#FBF8F1] border-[#E4DFD3]">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#17150F] mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#1E4D38]" />
                  Language Support
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    JavaScript / TypeScript
                  </div>
                  <div className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Python
                  </div>
                  <div className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Java / Kotlin
                  </div>
                  <div className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Go
                  </div>
                  <div className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    C / C++
                  </div>
                  <div className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    C# / .NET
                  </div>
                  <div className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Ruby
                  </div>
                  <div className="flex items-center gap-2 text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    PHP
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Paxley CLI */}
          <Card className="bg-[#FBF8F1] border-[#E4DFD3] mt-8">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-[#17150F] mb-4 flex items-center gap-2">
                <Terminal className="w-5 h-5 text-[#1E4D38]" />
                Three ways to run a scan
              </h3>
              <p className="text-[#17150F]/70 mb-4">
                No agents and no installation for an assessment. Point Paxley at the code the way
                that suits the engagement, and every engine runs in one pass.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-[#17150F]/80">
                  <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                  Upload a ZIP of the codebase
                </div>
                <div className="flex items-center gap-2 text-[#17150F]/80">
                  <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                  Paste a public Git URL
                </div>
                <div className="flex items-center gap-2 text-[#17150F]/80">
                  <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                  Connect the GitHub integration for continuous scans
                </div>
                <div className="flex items-center gap-2 text-[#17150F]/80">
                  <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                  Self-hosted packages run entirely in your environment
                </div>
              </div>
              <div className="mt-4 bg-[#141412] rounded-lg p-4 font-mono text-sm text-[#F5F1E6]/70">
                <span className="text-[#E4EFE4]">→</span> paxleysoftware.com/upload
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ==================== PAGE 5: Benefits ==================== */}
      <div className="min-h-screen p-8 md:p-16 bg-[#FBF8F1] page-break">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#17150F] mb-4">Why Paxley Is Different</h2>
            <p className="text-lg text-[#17150F]/70">
              Built for today&rsquo;s software security challenges, not yesterday&rsquo;s tool landscape.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {LEAD_DIFFERENTIATORS.map((d) => (
              <Card key={d.n} className="bg-white shadow-sm">
                <CardContent className="p-6">
                  <div className="text-sm font-mono text-[#17150F]/40">{d.n}</div>
                  <h3 className="mt-1 text-xl font-bold text-[#17150F]">{d.title}</h3>
                  <p className="mt-2 text-[#17150F]/70">{d.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Proof, not a testimonial: real output on public reference code. Customer
              quotes are added only once a customer has approved one in writing. */}
          <Card className="bg-[#1E4D38] border-0 text-[#F5F1E6]">
            <CardContent className="p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#F5F1E6]/70 mb-3">
                Checkable, not claimed
              </p>
              <p className="text-lg mb-4">
                On Microsoft&rsquo;s public eShopOnWeb reference application, Paxley resolves
                301 packages (34 declared, 267 transitive) and finds 9 vulnerable packages,
                8 of them reachable only through other dependencies. It flags the plaintext
                database password in the configuration file and reports the runtime as supported
                until November 2026.
              </p>
              <p className="text-sm text-[#F5F1E6]/70">
                Every figure above reproduces in the product. Nothing in this brochure is an
                invented statistic or an unnamed customer.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ==================== PAGE 6: Code Trust (NS-CTAF) ==================== */}
      <div className="min-h-screen p-8 md:p-16 page-break">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#17150F] mb-4">Code Trust Assurance</h2>
            <p className="text-lg text-[#17150F]/70 max-w-3xl mx-auto">
              Paxley is the evidence layer for the Nucleus Systems Code Trust Assurance Framework
              (NS-CTAF v1.0): six domains, {CONTROL_COUNT} controls, five maturity levels and a 0-100
              Trust Score, aligned with SLSA, NIST SSDF, OWASP SAMM, the EU CRA and EO 14028.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {DOMAINS.map((d) => (
              <Card key={d.id} className="bg-[#FBF8F1] border-[#E4DFD3]">
                <CardContent className="p-5">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-lg font-bold text-[#17150F]">
                      {d.id} {d.name}
                    </h3>
                    <span className="text-sm text-[#17150F]/60">{d.weight}%</span>
                  </div>
                  <p className="mt-1 text-xs font-semibold text-[#1E4D38]">
                    {d.controls.length} controls &bull; {COVERAGE_LABEL[d.coverage]}
                  </p>
                  <p className="mt-2 text-sm text-[#17150F]/70">{d.paxley}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-6 text-xs text-[#17150F]/50">
            Levels: CTA-1 Transparent, CTA-2 Verified, CTA-3 Assured, CTA-4 Adaptive Trust. Evidence
            mapping supports control review. It is not certification or an audit opinion.
          </p>
        </div>
      </div>

      {/* ==================== PAGE 7: Pricing & CTA ==================== */}
      <div className="min-h-screen p-8 md:p-16 page-break">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#17150F] mb-4">Simple, Transparent Pricing</h2>
            <p className="text-lg text-[#17150F]/70">
              Assess first, adopt when ready. Priced around your software estate, never per seat.
            </p>
          </div>

          {/* One-off assessment entry point */}
          <Card className="bg-[#E4EFE4] border border-[#1E4D38]/30 shadow-sm mb-8">
            <CardContent className="p-6 md:flex items-center justify-between gap-8">
              <div>
                <h3 className="text-xl font-bold text-[#17150F] mb-1">One-Off Code Security Risk Assessment</h3>
                <p className="text-[#17150F]/70 text-sm">
                  M&amp;A due diligence, investor underwriting and vendor risk review, with source code never
                  exposed, no platform contract. Custom report and IC / underwriting support available
                  for $500-1,500.
                </p>
              </div>
              <div className="mt-4 md:mt-0 text-right shrink-0">
                <span className="text-4xl font-bold text-[#17150F]">$1,500</span>
                <span className="text-[#17150F]/70"> / project</span>
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* Community */}
            <Card className="bg-white shadow-sm">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#17150F] mb-2">Community</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">Free</span>
                </div>
                <p className="text-[#17150F]/70 text-sm mb-6">Open source, solo developers and evaluation</p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Up to 3 repositories, 5 scans a month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    10 MB source processing
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    SAST, SCA and secrets
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    PR scanning
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Community support
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Starter */}
            <Card className="bg-white shadow-sm">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#17150F] mb-2">Starter</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">$199</span>
                  <span className="text-[#17150F]/70">/month</span>
                </div>
                <p className="text-[#17150F]/70 text-sm mb-6">Small teams · fewer than 5 products / repos</p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Up to 5 products / main repos
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    150 MB source processing
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Full scanning stack + SBOM
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    3 / 6 / 12-month terms
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Growth */}
            <Card className="bg-[#1E4D38] border-0 text-[#F5F1E6] shadow-lg relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#E4EFE4] text-[#1E4D38] text-xs font-bold rounded-full">
                MOST POPULAR
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2">Growth</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">$499</span>
                  <span className="text-[#F5F1E6]/80">/month</span>
                </div>
                <p className="text-[#F5F1E6]/80 text-sm mb-6">Medium teams · 5 to under 10 products / repos</p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#F5F1E6]/80" />
                    5 to 10 products / main repos
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#F5F1E6]/80" />
                    500 MB source processing
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#F5F1E6]/80" />
                    Governance & assurance reporting
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#F5F1E6]/80" />
                    6-month minimum term
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Large Enterprise */}
            <Card className="bg-white shadow-sm">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#17150F] mb-2">Large Enterprise</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold">$999</span>
                  <span className="text-[#17150F]/70">/month</span>
                </div>
                <p className="text-[#17150F]/70 text-sm mb-6">Unlimited self-hosted use for broader estates</p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Unlimited products & repos
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    M&amp;A / diligence evidence outputs
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    Dedicated support
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#17150F]/80">
                    <CheckCircle2 className="w-4 h-4 text-[#1E4D38]" />
                    12-month minimum term
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-sm text-[#17150F]/55 mb-16 -mt-10">
            All recurring packages are self-hosted installations, so source code stays under your
            control. SaaS pricing to be announced.
          </p>

          {/* CTA */}
          <Card className="bg-[#141412] border-0">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold text-[#F5F1E6] mb-4">Ready to Secure Your Code?</h3>
              <p className="text-[#17150F]/45 mb-8 max-w-xl mx-auto">
                Join hundreds of development teams who trust Paxley to protect their applications.
                Get started in minutes with our free tier.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <div className="text-[#F5F1E6]">
                  <div className="font-bold text-lg">paxleysoftware.com</div>
                  <div className="text-[#17150F]/45 text-sm">Start your free trial today</div>
                </div>
                <div className="text-[#17150F]/55">|</div>
                <div className="text-[#F5F1E6]">
                  <div className="font-bold text-lg">yash.sancheti@paxleysoftware.com</div>
                  <div className="text-[#17150F]/45 text-sm">Contact our sales team</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-[#E4DFD3] flex flex-col md:flex-row items-center justify-between text-sm text-[#17150F]/55">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <Shield className="w-5 h-5 text-[#1E4D38]" />
              <span className="font-bold text-[#17150F]">Paxley</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
              <span>paxleysoftware.com</span>
              <span className="hidden sm:inline">&bull;</span>
              <span className="break-all">yash.sancheti@paxleysoftware.com</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Brochure;